import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import useProSiteNavigationData from "../hooks/use-pro-site-navigation-data";
import useAllPartners from "../hooks/use-all-partners";
import { slugify } from "../utils/strings";

const BrandContext = createContext();

export const BrandProvider = ({ page, brandId, brands, children }) => {
  const {
    navigationSets,
    supertrieEvents,
    supertriEvents,
    allTeamPages,
    supertriLatest,
    supertrieLatest,
    supertriVideos,
    supertrieVideos,
  } = useProSiteNavigationData();
  const navigationSetSupertrie = navigationSets.nodes.filter(
    (set) => set.navigationSetName === "supertri > Footer > supertri E",
  );
  const navigationSetTrimajors = navigationSets.nodes.filter(
    (set) => set.navigationSetName === "supertri > Footer > TriMajors",
  );
  const { partnersSupertri, partnersSupertrie } = useAllPartners();
  const supertrieBrandId = "e1d95f21-6df4-505f-b413-8765376cbb08";
  const [brand, setBrand] = useState(() => () => {});
  const contentfulBrand = page?.brand;
  const navigationHeaderPrimary = useMemo(
    () => contentfulBrand?.navigationHeaderPrimary ?? [],
    [contentfulBrand],
  );
  const navigationHeaderSecondary = useMemo(
    () => contentfulBrand?.navigationHeaderSecondary ?? [],
    [contentfulBrand],
  );
  const navigationFooterPrimary = useMemo(
    () => contentfulBrand?.navigationFooterPrimary ?? [],
    [contentfulBrand],
  );
  const navigationFooterSecondary = useMemo(
    () => contentfulBrand?.navigationFooterSecondary ?? [],
    [contentfulBrand],
  );

  useEffect(() => {
    const selectedBrand = brandId
      ? brands?.find((brand) => brand.id === brandId)
      : brands && brands[0];

    const brandSlug = selectedBrand?.internalUrl?.slug
      ? slugify(selectedBrand.internalUrl.slug)
      : "";

    const newBrand = {
      ...selectedBrand,
      navigation: {
        navigationSetSupertrie,
        navigationSetTrimajors,
      },
      partners:
        selectedBrand?.id === supertrieBrandId
          ? partnersSupertrie?.partners
          : partnersSupertri?.partners,
      events:
        selectedBrand?.id === supertrieBrandId
          ? supertrieEvents
          : supertriEvents,
      teamPages: allTeamPages,
      latest:
        selectedBrand?.id === supertrieBrandId
          ? supertrieLatest
          : supertriLatest,
      videos:
        selectedBrand?.id === supertrieBrandId
          ? supertrieVideos
          : supertriVideos,
      brandId,
      brands: brands || [],
      header: {
        navigationSetPrimary: navigationHeaderPrimary,
        navigationSetSecondary: navigationHeaderSecondary,
      },
      footer: {
        navigationSetPrimary: navigationFooterPrimary,
        navigationSetSecondary: navigationFooterSecondary,
      },
      brandSlug,
      isNotBaseBrand: brandSlug !== "",
    };

    const mergedBrandDetails = { ...newBrand, ...contentfulBrand };

    // Prevent infinite loop by checking if brand actually changed
    if (JSON.stringify(brand) !== JSON.stringify(mergedBrandDetails)) {
      setBrand(mergedBrandDetails);
    }
  }, [
    brandId,
    brands,
    navigationSetSupertrie,
    navigationSetTrimajors,
    partnersSupertri?.partners,
    partnersSupertrie?.partners,
    supertriEvents,
    supertriLatest,
    supertriVideos,
    supertrieEvents,
    supertrieLatest,
    supertrieVideos,
    allTeamPages,
    navigationHeaderPrimary,
    navigationHeaderSecondary,
    navigationFooterPrimary,
    navigationFooterSecondary,
    contentfulBrand,
    brand,
  ]);

  return (
    <BrandContext.Provider value={{ brand, setBrand }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error("useBrand must be used within an BrandProvider");
  }
  return context;
};
